import React, { useState, useEffect } from 'react';
import { 
  Users, 
  MessageCircle, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Activity,
  MessageSquare,
  AlertCircle
} from 'lucide-react';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color, subtitle, loading }) => (
  <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-300">
    <div className="flex justify-between items-start">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon size={24} className={`text-${color.split('-')[1]}-600`} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {trendValue}
        </div>
      )}
    </div>
    <div className="mt-4">
      <h3 className="text-sm font-medium text-gray-500 dark:text-slate-400">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 dark:text-slate-100 mt-1">
        {loading ? '...' : value}
      </p>
      {subtitle && <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{subtitle}</p>}
    </div>
  </div>
);

function Dashboard() {
  const { t } = useLanguage();
  
  const [conversations, setConversations] = useState([]);
  const [metrics, setMetrics] = useState({
    total_conversations: 0,
    total_messages: 0,
    active_conversations: 0,
    avg_response_time_minutes: 0,
    today_conversations: 0,
    today_messages: 0,
    closed_today: 0
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUserData();
    loadData();
  }, []);

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUser(payload);
    } catch (err) {
      console.error('Erro ao carregar dados do usuário:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [conversationsRes, metricsRes] = await Promise.all([
        api.get('/api/conversations?limit=10'),
        api.get('/api/reports/dashboard')
      ]);
      
      setConversations(conversationsRes.data || []);
      setMetrics(metricsRes.data);
    } catch (err) {
      console.error('❌ Erro ao carregar dashboard:', err);
      setError(err.response?.data?.error || err.response?.data?.details || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatResponseTime = (minutes) => {
    if (!minutes || minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const isOperator = currentUser?.role === 'attendant';

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
            {isOperator 
              ? `${t('dashboard.helloOperator', { name: currentUser?.name || t('dashboard.operator') })}!` 
              : t('dashboard.welcome')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400">
            {isOperator 
              ? t('dashboard.operatorSubtitle') 
              : t('dashboard.subtitle')}
          </p>
        </div>
        {/* ⭐ CORRIGIDO: atendente não deve ver atalhos de Relatórios/Campanha
            (são recursos de gestão, não fazem parte do dia a dia dele) */}
        {!isOperator && (
        <div className="flex gap-3">
          <button 
            onClick={() => window.location.href = '/reports'}
            className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
            {t('dashboard.viewReports')}
          </button>
          <button 
            onClick={() => window.location.href = '/bulk-messaging'}
            className="px-4 py-2 bg-green-600 rounded-xl text-sm font-medium text-white hover:bg-green-700 shadow-lg shadow-green-200 transition-all">
            {t('dashboard.newCampaign')}
          </button>
        </div>
        )}
      </div>

      {/* Banner de Erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-900">{t('dashboard.errorLoading')}</p>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
          <button 
            onClick={loadData}
            className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700">
            {t('dashboard.tryAgain')}
          </button>
        </div>
      )}

      {/* Banner do Operador */}
      {isOperator && metrics.my_total_conversations !== undefined && (
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <MessageSquare size={32} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{t('dashboard.yourStats')}</h2>
              <p className="opacity-90">{t('dashboard.keepGoodWork')}</p>
            </div>
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold">{metrics.my_total_conversations || 0}</p>
                <p className="text-xs opacity-80">{t('dashboard.total')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{metrics.my_closed_conversations || 0}</p>
                <p className="text-xs opacity-80">{t('dashboard.resolved')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold">
                  {metrics.my_avg_response_minutes || 0}m
                </p>
                <p className="text-xs opacity-80">{t('dashboard.avgTime')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title={t('dashboard.totalConversations')} 
          value={metrics.total_conversations.toLocaleString()}
          icon={MessageCircle} 
          color="bg-blue-500"
          subtitle={`${metrics.active_conversations} ${t('dashboard.activeNow')}`}
          loading={loading}
        />
        <StatCard 
          title={t('dashboard.totalMessages')} 
          value={metrics.total_messages.toLocaleString()}
          icon={TrendingUp} 
          color="bg-green-500"
          subtitle={`${metrics.today_messages || 0} ${t('dashboard.today')}`}
          loading={loading}
        />
        <StatCard 
          title={t('dashboard.activeConversations')} 
          value={metrics.active_conversations.toLocaleString()}
          icon={Activity} 
          color="bg-purple-500"
          subtitle={t('dashboard.inProgress')}
          loading={loading}
        />
        <StatCard 
          title={t('dashboard.avgResponseTime')} 
          value={formatResponseTime(metrics.avg_response_time_minutes)}
          icon={Clock} 
          color="bg-orange-500"
          subtitle={`${metrics.today_conversations || 0} ${t('dashboard.conversationsToday')}`}
          loading={loading}
        />
      </div>

      {/* Quick Actions — ⭐ CORRIGIDO: são recursos de gestão, atendente não deve ver */}
      {!isOperator && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl text-white shadow-lg">
          <BarChart3 size={32} className="mb-3" />
          <h3 className="font-bold text-lg mb-1">{t('dashboard.advancedReports')}</h3>
          <p className="text-sm opacity-90 mb-4">{t('dashboard.detailedMetrics')}</p>
          <button 
            onClick={() => window.location.href = '/reports'}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors">
            {t('dashboard.viewReportsArrow')}
          </button>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl text-white shadow-lg">
          <Users size={32} className="mb-3" />
          <h3 className="font-bold text-lg mb-1">{t('dashboard.leadsCRM')}</h3>
          <p className="text-sm opacity-90 mb-4">{t('dashboard.manageLeads')}</p>
          <button 
            onClick={() => window.location.href = '/leads'}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors">
            {t('dashboard.manageLeadsArrow')}
          </button>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-2xl text-white shadow-lg">
          <CheckCircle2 size={32} className="mb-3" />
          <h3 className="font-bold text-lg mb-1">{t('dashboard.automations')}</h3>
          <p className="text-sm opacity-90 mb-4">{t('dashboard.configureAuto')}</p>
          <button 
            onClick={() => window.location.href = '/automations'}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors">
            {t('dashboard.configureArrow')}
          </button>
        </div>
      </div>
      )}

      {/* Recent Conversations Table — só aparece pro admin, não pro atendente */}
      {!isOperator && (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50 dark:border-slate-700 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">{t('dashboard.recentConversations')}</h2>
          <button 
            onClick={() => window.location.href = '/conversations'}
            className="text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300">
            {t('dashboard.viewAll')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-900 text-gray-500 dark:text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-6 py-4 font-semibold">{t('dashboard.contact')}</th>
                <th className="px-6 py-4 font-semibold">{t('dashboard.lastMessage')}</th>
                <th className="px-6 py-4 font-semibold">{t('dashboard.dateTime')}</th>
                <th className="px-6 py-4 font-semibold">{t('dashboard.status')}</th>
                <th className="px-6 py-4 font-semibold">{t('dashboard.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-400">{t('dashboard.loadingConversations')}</td>
                </tr>
              ) : conversations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                    <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
                    <p>{t('dashboard.noConversations')}</p>
                  </td>
                </tr>
              ) : (
                conversations.slice(0, 10).map((conv) => (
                  <tr key={conv.id} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center justify-center font-bold text-xs">
                          {(conv.contact_name || conv.profile_name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                            {conv.contact_name || conv.profile_name || t('dashboard.user')}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">{conv.contact_phone || conv.contact_wa_id || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600 truncate max-w-xs">
                        {conv.last_message || t('dashboard.noMessages')}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {(conv.last_message_at || conv.updated_at) 
                        ? new Date(conv.last_message_at || conv.updated_at).toLocaleString('pt-BR') 
                        : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        conv.status === 'open' ? 'bg-green-50 text-green-600' :
                        conv.status === 'closed' ? 'bg-gray-50 text-gray-600' :
                        'bg-yellow-50 text-yellow-600'
                      }`}>
                        {conv.status === 'open' ? t('dashboard.statusActive') : 
                         conv.status === 'closed' ? t('dashboard.statusClosed') : 
                         conv.status || t('dashboard.statusPending')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <a 
                        href={`/conversation/${conv.id}`} 
                        className="text-green-600 hover:text-green-700 font-medium text-sm">
                        {t('dashboard.openChat')}
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

export default Dashboard;
